import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { useUserProfile } from '../../hooks/useUserProfile';
import { useUserVisibleThings } from '../../hooks/useUserVisibleThings';
import { useUserVisibleRequests } from '../../hooks/useUserVisibleRequests';
import MyItemsPanel from '../items/MyItemsPanel';
import './UserDetailPage.css';

function UserDetailPage({
  user,
  myGroups,
  myThings,
  setMyThings,
  myThingsLoading,
  myThingsError,
  myRequests,
  setMyRequests,
  myRequestsLoading,
  myRequestsError,
  onThingAdded,
  onRequestAdded,
  onSelectThing,
  onSelectRequest,
}) {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeItemsTab = searchParams.get('tab') === 'requests' ? 'requests' : 'things';
  const isSelf = user?.id === userId;
  const myGroupIds = new Set((myGroups ?? []).map((g) => g.id));
  const [userGroups, setUserGroups] = useState([]);
  const [userGroupsLoading, setUserGroupsLoading] = useState(true);
  const [userGroupsError, setUserGroupsError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    async function loadGroups() {
      if (isSelf) {
        setUserGroups(myGroups ?? []);
        setUserGroupsLoading(false);
        setUserGroupsError(null);
        return;
      }
      try {
        setUserGroupsLoading(true);
        setUserGroupsError(null);
        const { getGroupMembershipsForUser, getGroupsByIds } = await import('../../services/groupsService');
        const memberships = await getGroupMembershipsForUser(userId);
        if (!isMounted) return;
        const groupIds = memberships.map((r) => r.group_id);
        if (groupIds.length === 0) {
          setUserGroups([]);
          setUserGroupsLoading(false);
          return;
        }
        const roleByGroupId = {};
        memberships.forEach((m) => {
          roleByGroupId[m.group_id] = m.role;
        });
        const groupData = await getGroupsByIds(groupIds, { withInvite: true });
        if (!isMounted) return;
        const groupsWithRole = (groupData ?? []).map((g) => ({
          ...g,
          myRole: roleByGroupId[g.id] ?? null,
        }));
        setUserGroups(groupsWithRole);
      } catch (err) {
        if (!isMounted) return;
        setUserGroupsError(err.message || 'Failed to load groups.');
      } finally {
        if (!isMounted) return;
        setUserGroupsLoading(false);
      }
    }
    loadGroups();
    return () => { isMounted = false; };
  }, [isSelf, myGroups, userId]);

  function handleItemsTabChange(tab) {
    if (tab === 'requests') {
      setSearchParams({ tab: 'requests' }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }
  const { fullName, contactInfo, loading: profileLoading, error: profileError, refetch: refetchProfile } = useUserProfile(userId);
  const { things, loading: thingsLoading, error: thingsError } = useUserVisibleThings(userId);
  const { requests, loading: requestsLoading, error: requestsError } = useUserVisibleRequests(userId);

  const [editingProfile, setEditingProfile] = useState(false);
  const [editFullName, setEditFullName] = useState('');
  const [editContactInfo, setEditContactInfo] = useState('');
  const [saveError, setSaveError] = useState(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const [groupsExpanded, setGroupsExpanded] = useState(false);

  useEffect(() => {
    if (editingProfile) {
      setEditFullName(fullName ?? '');
      setEditContactInfo(contactInfo ?? '');
      setSaveError(null);
      setSaveMessage('');
    }
  }, [editingProfile, fullName, contactInfo]);

  async function handleSaveProfile(e) {
    e.preventDefault();
    setSaveError(null);
    setSaveMessage('');
    const nextFullName = editFullName.trim();
    const nextContactInfo = editContactInfo.trim();
    if (!nextFullName) {
      setSaveError('Display name is required.');
      return;
    }
    setSaving(true);
    try {
      const { error: upsertError } = await supabase.from('profiles').upsert({
        id: userId,
        full_name: nextFullName,
        contact_info: nextContactInfo || null,
      });
      if (upsertError) throw upsertError;
      setSaveMessage('Profile saved.');
      await refetchProfile();
      setEditingProfile(false);
    } catch (err) {
      setSaveError(err.message || 'Failed to save profile.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteAccount() {
    setDeleteError(null);
    setDeleteSubmitting(true);
    try {
      const { error } = await supabase.from('profiles').delete().eq('id', userId);
      if (error) throw error;
      setDeleteConfirmOpen(false);
      navigate('/', { replace: true });
      await supabase.auth.signOut();
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete profile.');
    } finally {
      setDeleteSubmitting(false);
    }
  }

  if (profileLoading && !fullName && !profileError) {
    return (
      <div className="App-main">
        <p className="status">Loading…</p>
      </div>
    );
  }

  if (profileError && !fullName) {
    return (
      <div className="App-main">
        <p className="status error" role="alert">{profileError}</p>
        <button type="button" className="back-link" onClick={() => navigate(-1)}>
          ← Back
        </button>
      </div>
    );
  }

  return (
    <div className="user-detail-page">
      <button type="button" className="back-link" onClick={() => navigate(-1)}>
        ← Back
      </button>
      <header className="user-detail-header">
        <div className="user-detail-name-row">
          <h2 className="user-detail-name">{fullName || 'New user'}</h2>
          {isSelf && (
            <>
              <span className="user-detail-badge">You</span>
              {!editingProfile && (
                <>
                  <button
                    type="button"
                    className="header-button user-detail-edit-btn"
                    onClick={() => setEditingProfile(true)}
                  >
                    Edit profile
                  </button>
                  <button
                    type="button"
                    className="header-button user-detail-delete-account-btn"
                    onClick={() => { setDeleteConfirmOpen(true); setDeleteError(null); }}
                  >
                    Delete account
                  </button>
                </>
              )}
            </>
          )}
        </div>
        {isSelf && editingProfile ? (
          <form onSubmit={handleSaveProfile} className="user-detail-edit-form" aria-label="Edit profile">
            {saveError && <p className="form-error" role="alert">{saveError}</p>}
            {saveMessage && <p className="user-detail-save-message">{saveMessage}</p>}
            <label className="form-label" htmlFor="user-edit-name">Display name</label>
            <input
              id="user-edit-name"
              type="text"
              className="form-input"
              value={editFullName}
              onChange={(e) => setEditFullName(e.target.value)}
              placeholder="Your name"
              disabled={saving}
              autoComplete="name"
              required
            />
            <label className="form-label" htmlFor="user-edit-contact">Contact info (optional)</label>
            <p className="form-hint">
              General location (part of town) and how others can reach you (phone, email, <a href="https://signal.org/blog/phone-number-privacy-usernames/" target="_blank" rel="noopener noreferrer">signal username</a>, etc.)
            </p>
            <textarea
              id="user-edit-contact"
              className="form-input form-textarea"
              value={editContactInfo}
              onChange={(e) => setEditContactInfo(e.target.value)}
              placeholder="Downtown. Phone: 867-5309"
              rows={3}
              disabled={saving}
            />
            <div className="user-detail-edit-actions">
              <button type="submit" className="submit-button" disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                className="header-button"
                onClick={() => { setEditingProfile(false); setSaveError(null); }}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : null}
        {isSelf && user?.email && !editingProfile && (
          <p className="user-detail-email">
            <span className="user-detail-contact-label">Email:</span> {user.email}
          </p>
        )}
        {!editingProfile && contactInfo && (
          <p className="user-detail-contact">
            <span className="user-detail-contact-label">Contact:</span> {contactInfo}
          </p>
        )}
      </header>

      <section className="user-detail-groups" aria-label="Groups">
        <button
          type="button"
          className="user-detail-groups-toggle"
          onClick={() => setGroupsExpanded((v) => !v)}
          aria-expanded={groupsExpanded}
          aria-controls="user-detail-groups-content"
        >
          <span className="user-detail-groups-toggle-icon" aria-hidden="true">
            {groupsExpanded ? '▼' : '▶'}
          </span>
          <span className="user-detail-groups-toggle-title">
            {userGroupsLoading
              ? 'Member of … groups'
              : `Member of ${userGroups.length} group${userGroups.length === 1 ? '' : 's'}`}
          </span>
        </button>
        <div
          id="user-detail-groups-content"
          className="user-detail-groups-content"
          hidden={!groupsExpanded}
        >
          {userGroupsLoading && <p className="status">Loading groups…</p>}
          {userGroupsError && (
            <p className="status error" role="alert">
              {userGroupsError}
            </p>
          )}
          {!userGroupsLoading && !userGroupsError && userGroups.length === 0 && (
            <p className="status">Not a member of any groups yet.</p>
          )}
          {!userGroupsLoading && !userGroupsError && userGroups.length > 0 && (
            <ul className="user-detail-groups-list" aria-label="Groups">
              {userGroups.map((g) => (
                <li key={g.id} className="user-detail-group-item">
                  <button
                    type="button"
                    className="user-detail-group-item-button"
                    onClick={() => navigate(`/groups/${g.id}`)}
                  >
                    <span className="user-detail-group-name">{g.name}</span>
                    {!isSelf && myGroupIds.has(g.id) && (
                      <span
                        className="user-detail-group-mutual-badge"
                        aria-label="You are both in this group"
                      >
                        Mutual
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {isSelf && (
        <section className="user-detail-my-things-section" aria-label="My things and requests">
          <MyItemsPanel
            user={user}
            myThings={myThings}
            setMyThings={setMyThings}
            myThingsLoading={myThingsLoading}
            myThingsError={myThingsError}
            myRequests={myRequests}
            setMyRequests={setMyRequests}
            myRequestsLoading={myRequestsLoading}
            myRequestsError={myRequestsError}
            onThingAdded={onThingAdded}
            onRequestAdded={onRequestAdded}
            onSelectThing={onSelectThing}
            onSelectRequest={onSelectRequest}
            canAddItems={true}
            activeTab={activeItemsTab}
            onTabChange={handleItemsTabChange}
          />
        </section>
      )}

      {!isSelf && (
        <section className="user-detail-things" aria-label="Things">
          <h3 className="map-section-title">Things you can check out</h3>
          {thingsLoading && <p className="status">Loading things…</p>}
          {thingsError && <p className="status error" role="alert">{thingsError}</p>}
          {!thingsLoading && !thingsError && things.length === 0 && (
            <p className="status">No things here.</p>
          )}
          {!thingsLoading && !thingsError && things.length > 0 && (
            <ul className="user-detail-things-list" aria-label="Things">
              {things.map((thing) => (
                <li key={thing.id}>
                  <button
                    type="button"
                    className="thing-card thing-card-clickable"
                    onClick={() => navigate(`/thing/${thing.id}`, { state: { thing } })}
                  >
                    <div className="thing-name">{thing.name}</div>
                    {thing.description && (
                      <div className="thing-description">{thing.description}</div>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {!isSelf && (
        <section className="user-detail-things" aria-label="Requests">
          <h3 className="map-section-title">Requests you can respond to</h3>
          {requestsLoading && <p className="status">Loading requests…</p>}
          {requestsError && <p className="status error" role="alert">{requestsError}</p>}
          {!requestsLoading && !requestsError && requests.length === 0 && (
            <p className="status">No requests here.</p>
          )}
          {!requestsLoading && !requestsError && requests.length > 0 && (
            <ul className="user-detail-things-list" aria-label="Requests">
              {requests.map((request) => (
                <li key={request.id}>
                  <button
                    type="button"
                    className="thing-card thing-card-clickable"
                    onClick={() => navigate(`/thing/${request.id}`, { state: { thing: request } })}
                  >
                    <div className="thing-name">{request.name}</div>
                    {request.description && (
                      <div className="thing-description">{request.description}</div>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {deleteConfirmOpen && (
        <div className="user-detail-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="delete-account-modal-title">
          <div className="user-detail-modal-card">
            <h3 id="delete-account-modal-title" className="user-detail-modal-title">Delete account?</h3>
            <p className="user-detail-modal-text">This cannot be undone. Your profile, things, and group memberships will be removed.</p>
            {deleteError && (
              <p className="form-error user-detail-modal-error" role="alert">{deleteError}</p>
            )}
            <div className="user-detail-modal-actions">
              <button
                type="button"
                className="header-button"
                onClick={() => { setDeleteConfirmOpen(false); setDeleteError(null); }}
                disabled={deleteSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="user-detail-modal-delete-button"
                onClick={handleDeleteAccount}
                disabled={deleteSubmitting}
              >
                {deleteSubmitting ? 'Deleting…' : 'Delete account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserDetailPage;
